import { useCallback, useMemo, useRef, useState } from "react";

import {
  useSidebarPinOrder,
  type SidebarPinOrder,
} from "@/hooks/use-sidebar-pin-order";
import type { AccessTokenGetter } from "@/lib/utils";

/**
 * Display-order merge for one pinned collection.
 *
 * Promoted IDs come first, in their caller-supplied order. Explicitly ordered
 * IDs follow in the saved (or overridden) order. Every other pinned ID keeps
 * its original server order after them, so newly pinned entries and
 * never-ordered pins always stay visible.
 */
export function mergePinOrder(
  serverIds: readonly string[],
  savedOrder: readonly string[] | null,
  promotedIds: readonly string[] = [],
): string[] {
  const ordered: string[] = [];
  const seen = new Set<string>();
  for (const id of promotedIds) {
    if (!serverIds.includes(id) || seen.has(id)) continue;
    seen.add(id);
    ordered.push(id);
  }
  if (!savedOrder || savedOrder.length === 0) {
    for (const id of serverIds) {
      if (!seen.has(id)) {
        seen.add(id);
        ordered.push(id);
      }
    }
    return ordered;
  }
  for (const id of savedOrder) {
    if (!serverIds.includes(id) || seen.has(id)) continue;
    seen.add(id);
    ordered.push(id);
  }
  for (const id of serverIds) {
    if (!seen.has(id)) ordered.push(id);
  }
  return ordered;
}

/**
 * Select a root landing target without ever surfacing a stale or RBAC-hidden
 * raw pin. `orderedPinIds` must already be the same display order used by the
 * sidebar.
 */
export function firstAccessiblePinnedAgentId(
  orderedPinIds: readonly string[],
  accessibleAgentIds: ReadonlySet<string>,
): string | null {
  return orderedPinIds.find((agentId) => accessibleAgentIds.has(agentId)) ?? null;
}

export interface SidebarPinOrderingApi {
  /** True once the saved preference has either loaded or safely fallen back. */
  loaded: boolean;
  /** Display order for the given collection's current pin IDs. `promotedIds`
   * always stay ahead of the saved user order at render time. */
  displayIds: (
    serverIds: readonly string[],
    key: "agentIds" | "projectIds",
    promotedIds?: readonly string[],
  ) => string[];
  /** Applies a drop and persists the combined order for both collections. */
  reorder: (key: "agentIds" | "projectIds", orderedIds: string[]) => void;
  /**
   * Non-null when the display order could not be persisted ("unavailable":
   * the preference read failed, likely an older bridge; "save-failed": the
   * most recent save failed). Sections show a non-blocking notice.
   */
  persistError: string | null;
}

/**
 * Owns the sidebar pin display order for both pinned collections so every
 * drop persists one coherent combined document (agent and project orders
 * together) instead of letting two sections clobber each other's half.
 *
 * The latest desired orders are kept in a ref and written synchronously, so a
 * fast succession of drops in both collections still saves the final state.
 */
export function useSidebarPinOrdering(
  getToken: AccessTokenGetter | null,
): SidebarPinOrderingApi {
  const { order, loaded, loadError, saveError, save } = useSidebarPinOrder(getToken);
  const desiredRef = useRef<SidebarPinOrder>({ agentIds: [], projectIds: [] });
  // `order` changes only when a server snapshot arrives from a GET. Track its
  // identity so a render caused by an optimistic reorder cannot put the
  // still-stale snapshot back into desiredRef.
  const appliedServerOrderRef = useRef<SidebarPinOrder | null>(null);
  const [renderVersion, setRenderVersion] = useState<number>(0);
  const [saveFailureCount, setSaveFailureCount] = useState(0);
  const saveFailedRef = useRef(false);
  const reorderGenerationRef = useRef(0);

  if (loaded && !loadError && order !== appliedServerOrderRef.current) {
    desiredRef.current = {
      agentIds: order?.agentIds ?? [],
      projectIds: order?.projectIds ?? [],
    };
    appliedServerOrderRef.current = order;
  }

  const saveFailed = saveError !== null || saveFailedRef.current;
  const persistError = loadError
    ? "unavailable"
    : saveFailed
      ? "save-failed"
      : null;

  const reorder = useCallback(
    (key: "agentIds" | "projectIds", orderedIds: string[]) => {
      const reorderGeneration = reorderGenerationRef.current + 1;
      reorderGenerationRef.current = reorderGeneration;
      const next: SidebarPinOrder = {
        ...desiredRef.current,
        [key]: orderedIds,
      };
      desiredRef.current = next;
      saveFailedRef.current = false;
      // Refs do not trigger renders; bump a version so the optimistic order
      // is visible immediately, before the save round-trip settles.
      setRenderVersion((version) => version + 1);
      void save(next).then((ok) => {
        if (reorderGeneration !== reorderGenerationRef.current) return;
        saveFailedRef.current = !ok;
        setSaveFailureCount((count) => count + 1);
      });
    },
    [save],
  );

  const displayIds = useCallback(
    (
      serverIds: readonly string[],
      key: "agentIds" | "projectIds",
      promotedIds: readonly string[] = [],
    ) => mergePinOrder(serverIds, desiredRef.current[key], promotedIds),
    // desiredRef is intentionally read during render: it always holds the
    // newest desired order after a drop. renderVersion is bumped on every
    // drop (and save settlement), which is what makes this recompute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [renderVersion],
  );

  return useMemo(
    () => ({ loaded, displayIds, reorder, persistError }),
    [displayIds, loaded, reorder, persistError, saveFailureCount],
  );
}
